import { useState } from 'react'
import { gql, useQuery, useApolloClient, useReactiveVar } from '@apollo/client'
import { API } from 'aws-amplify'
import { LoadingButton } from '@mui/lab'
import { Stack, Typography, Chip, Divider } from '@mui/material'
import onCreateInvite from '../../invite/onCreate'
import { userVar, userEntityRelationVar } from '../../../../client/cache'

export const FEATURE_GRANTS = [
  { role: 'auditor', label: 'Audit Open Quantities' },
  { role: 'supply_analyst', label: 'Supply vs Demand' },
  { role: 'inv_check', label: 'Inventory Check' },
]

const inviteByUser = /* GraphQL */ `
  query InviteByUser(
    $userID: ID!
    $entityID: ModelIDKeyConditionInput
    $limit: Int
    $nextToken: String
  ) {
    inviteByUser(
      userID: $userID
      entityID: $entityID
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        role
        status
        userID
        entityID
      }
      nextToken
    }
  }
`

export default function FeatureAccess({ userData }) {
  const { entityID } = useReactiveVar(userEntityRelationVar)
  const currentUser = useReactiveVar(userVar)
  const client = useApolloClient()

  const { id: userID, fn, ln, email } = userData

  const entityRelation = userData?.entityUserRelations?.items?.find(
    (x) => x.entity?.id === entityID,
  )
  const entityAbbr =
    entityRelation?.entity?.alias || entityRelation?.entity?.name || ''

  const { data: inviteData, refetch } = useQuery(gql(inviteByUser), {
    variables: { userID, entityID: { eq: entityID } },
    skip: !userID || !entityID,
    fetchPolicy: 'network-only',
  })

  if (!inviteData) return null

  const allInvites = inviteData?.inviteByUser?.items || []

  return (
    <Stack spacing={1} bgcolor='#f1f1f1' borderRadius={1} padding={2}>
      <Typography variant='h6' sx={{ alignSelf: 'center' }}>
        Feature Access
      </Typography>
      <Divider />
      {FEATURE_GRANTS.map((feature) => (
        <FeatureLine
          key={feature.role}
          feature={feature}
          invites={allInvites.filter(
            (x) => x.role?.toLowerCase() === feature.role.toLowerCase(),
          )}
          userID={userID}
          fn={fn}
          ln={ln}
          email={email}
          entityID={entityID}
          entityAbbr={entityAbbr}
          currentUser={currentUser}
          client={client}
          refetch={refetch}
        />
      ))}
    </Stack>
  )
}

function FeatureLine({
  feature,
  invites,
  userID,
  fn,
  ln,
  email,
  entityID,
  entityAbbr,
  currentUser,
  client,
  refetch,
}) {
  const [loading, setLoading] = useState(false)

  const activeInvite = invites.find((x) => x.status !== 'REMOVED')

  async function handleGrant() {
    setLoading(true)
    try {
      await onCreateInvite({
        client,
        values: {
          email,
          entityID,
          entityName: entityAbbr,
          role: feature.role,
          ln,
          fn,
          inviterID: currentUser.id,
          inviterFn: currentUser.fn,
          inviterLn: currentUser.ln,
        },
      })
      await refetch()
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  async function handleRevoke() {
    setLoading(true)
    try {
      await API.post('iSupplyAPI', '/restricted', {
        body: {
          operation: 'REMOVE_FROM_GROUP',
          meta: { invitationID: activeInvite.id },
        },
      })
      await refetch()
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      sx={{ py: 0.5 }}>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Typography sx={{ width: 200 }}>{feature.label}</Typography>
        <Chip
          size='small'
          label={activeInvite ? activeInvite.status : 'NO ACCESS'}
          color={
            activeInvite?.status === 'ACCEPTED'
              ? 'success'
              : activeInvite
                ? 'warning'
                : 'default'
          }
        />
      </Stack>
      <LoadingButton
        size='small'
        variant='contained'
        color={activeInvite ? 'secondary' : 'primary'}
        loading={loading}
        onClick={activeInvite ? handleRevoke : handleGrant}>
        {activeInvite ? 'revoke' : 'grant'}
      </LoadingButton>
    </Stack>
  )
}
